"use client"

import { Box, SimpleGrid, Text } from "@chakra-ui/react"
import { useEffect, useMemo, useRef, useState } from "react"

import BlurReveal from "@/components/Animations/BlurReveal"
import type { HomeMetric } from "@/types/getHome"

interface StatsProps {
  metrics: HomeMetric[]
}

interface ParsedNumericStat {
  finalValueText: string
  fractionDigits: number
  suffix: string
  target: number
  type: "number"
}

interface ParsedTextStat {
  text: string
  type: "text"
}

type ParsedStat = ParsedNumericStat | ParsedTextStat

const NUMBER_FORMATTER = new Intl.NumberFormat("en-GB")

const formatNumber = (value: number, fractionDigits: number) =>
  fractionDigits > 0
    ? value.toLocaleString("en-GB", {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
      })
    : NUMBER_FORMATTER.format(value)

const parseStat = (stat: string): ParsedStat => {
  const text = stat.trim()
  const match = text.match(/^([+-]?\d[\d,]*(?:\.\d+)?)(.*)$/)

  if (!match) {
    return { text, type: "text" }
  }

  const rawNumber = match[1] ?? ""
  const target = Number(rawNumber.replace(/,/g, ""))

  if (!Number.isFinite(target)) {
    return { text, type: "text" }
  }

  const suffix = (match[2] ?? "").trim()
  const fractionDigits = rawNumber.includes(".")
    ? (rawNumber.split(".")[1]?.length ?? 0)
    : 0

  return {
    finalValueText: formatNumber(target, fractionDigits),
    fractionDigits,
    suffix,
    target,
    type: "number",
  }
}

const easeOutExpo = (progress: number) =>
  progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress)

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

function MetricValue({
  shouldAnimate,
  stat,
}: {
  shouldAnimate: boolean
  stat: string
}) {
  const parsedStat = useMemo(() => parseStat(stat), [stat])
  const [animatedValue, setAnimatedValue] = useState<{
    stat: string
    value: string
  } | null>(null)

  useEffect(() => {
    if (parsedStat.type === "text" || !shouldAnimate) {
      return
    }

    if (prefersReducedMotion()) {
      return
    }

    const duration = 1200
    const start = performance.now()
    let frameId = 0

    const animate = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      const easedProgress = easeOutExpo(progress)
      const current = parsedStat.target * easedProgress

      setAnimatedValue({
        stat,
        value: formatNumber(
          parsedStat.fractionDigits > 0 ? current : Math.round(current),
          parsedStat.fractionDigits
        ),
      })

      if (progress < 1) {
        frameId = requestAnimationFrame(animate)
        return
      }

      setAnimatedValue({
        stat,
        value: formatNumber(parsedStat.target, parsedStat.fractionDigits),
      })
    }

    frameId = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [parsedStat, shouldAnimate, stat])

  if (parsedStat.type === "text") {
    return <>{parsedStat.text}</>
  }

  const finalDisplayValue = formatNumber(
    parsedStat.target,
    parsedStat.fractionDigits
  )
  const animationDisplayValue =
    animatedValue?.stat === stat ? animatedValue.value : null
  const displayValue =
    shouldAnimate && !prefersReducedMotion()
      ? (animationDisplayValue ?? formatNumber(0, parsedStat.fractionDigits))
      : finalDisplayValue

  return (
    <>
      <Box
        as="span"
        display="inline-flex"
        // minW={`${parsedStat.finalValueText.length - 1}ch`}
      >
        {displayValue}
      </Box>
      {parsedStat.suffix && (
        <Text
          as="span"
          color="fg.muted"
          display="inline-flex"
          fontSize="0.45em"
          fontStyle="italic"
          lineHeight="1"
          ml={1}
          verticalAlign="super"
        >
          {parsedStat.suffix}
        </Text>
      )}
    </>
  )
}

const Stats2 = ({ metrics }: StatsProps) => {
  const sectionRef = useRef<HTMLElement | null>(null)
  const [shouldAnimate, setShouldAnimate] = useState(false)

  useEffect(() => {
    const section = sectionRef.current

    if (!section) {
      return
    }

    if (!("IntersectionObserver" in window)) {
      const frameId = requestAnimationFrame(() => {
        setShouldAnimate(true)
      })

      return () => {
        cancelAnimationFrame(frameId)
      }
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return
        }

        setShouldAnimate(true)
        observer.unobserve(entry.target)
      },
      { threshold: 0.35 }
    )

    observer.observe(section)

    return () => {
      observer.disconnect()
    }
  }, [])

  if (metrics.length === 0) {
    return null
  }

  return (
    <Box
      aria-label="Key metrics"
      as="section"
      borderBottomColor="border"
      borderBottomWidth="1px"
      borderTopColor="border"
      borderTopWidth="1px"
      id="metrics"
      ref={sectionRef}
      w="full"
    >
      <SimpleGrid
        borderColor="border"
        borderInlineWidth="1px"
        columns={{ base: 2, lg: 4 }}
        maxW="1440px"
        mx="auto"
        px={{ base: 6, lg: 0 }}
      >
        {metrics.map((metric, index) => (
          <BlurReveal
            animateWhen={shouldAnimate}
            delay={index * 0.08}
            key={`${metric.stat}-${metric.description}`}
            style={{ height: "100%", minWidth: 0 }}
          >
            <Box
              borderColor="border"
              borderLeftWidth={{
                base: index % 2 === 0 ? "0" : "1px",
                lg: index % 4 === 0 ? "0" : "1px",
              }}
              borderTopWidth={{ base: index >= 2 ? "1px" : "0", lg: "0" }}
              h="full"
              minW={0}
              p={{ base: 8, lg: 10 }}
            >
              <Text
                as="p"
                color="fg"
                display="inline-flex"
                fontFamily="heading"
                fontSize={{
                  base: "xx-large",
                  sm: "xxx-large",
                  md: "xx-large",
                  xl: "xxx-large",
                }}
                fontVariantNumeric="tabular-nums"
                fontWeight="400"
                letterSpacing="0"
                lineHeight="0.9"
              >
                <MetricValue shouldAnimate={shouldAnimate} stat={metric.stat} />
              </Text>
              <Text
                as="p"
                color="fg.muted"
                fontFamily="mono"
                fontSize="14px"
                letterSpacing="0"
                lineHeight="1.5"
                mt={4}
                textTransform="uppercase"
                whiteSpace="pre-line"
              >
                {metric.description}
              </Text>
            </Box>
          </BlurReveal>
        ))}
      </SimpleGrid>
    </Box>
  )
}

export default Stats2
